import React, { Fragment, useEffect, useState } from "react";
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild, Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import TimeAgo from "react-timeago";
import { MdKeyboardVoice, MdPhotoCamera, MdVideocam, MdMoreHoriz, MdClose } from "react-icons/md";
import { FaEdit, FaTrash, FaRegCommentDots } from "react-icons/fa";

import { delete_comment } from "../api";
import { getMediaUrl } from "../lib/utils";

import CommentPhoto from "./comments/CommentPhoto";
import CommentVideo from "./comments/CommentVideo";
import CommentVoice from "./comments/CommentVoice";

import Reply from "./Reply";
import ShowReply from "./ShowReply";
import CommentInfo from "./CommentInfo";
import ToastMessage from "./ToastMessage";

const ShowComment = ({ isOpen, isClose, allComments, comment_numb, onEditComment }) => {
  const [localComments, setLocalComments] = useState([]);
  const [selectedComment, setSelectedComment] = useState();
  const [replyType, setReplyType] = useState();
  const [showReply, setShowReply] = useState(false);
  const [toggleOpen, setToggleOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);

  useEffect(() => {
    setLocalComments(allComments || []);
  }, [allComments]);

  const again = () => setShowReply((prev) => !prev);

  const openReply = (type, comment) => {
    setReplyType(type);
    setSelectedComment(comment);
    setToggleOpen(true);
  };

  const editComment = (comment) => {
    setSelectedComment(comment);
    if (onEditComment) onEditComment(comment);
  };

  const openDeleteModal = (comment) => {
    setCommentToDelete(comment);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setCommentToDelete(null);
  };

  const confirmDeleteComment = () => {
    if (!commentToDelete?.id) return;

    const id = commentToDelete.id;

    delete_comment(id)
      .then((res) => {
        if (res.data.message) {
          setLocalComments((prev) => prev.filter((comment) => comment?.id !== id));
          ToastMessage(res.data.message);
          closeDeleteModal();
        }
      })
      .catch((error) => {
        console.error("Delete comment error:", error);
        ToastMessage(error?.response?.data?.message || "Unable to delete comment.");
      });
  };

  const closeComments = () => {
    setToggleOpen(false);
    setShowReply(false);
    setSelectedComment(undefined);
    isClose();
  };

  return (
    <>
      {/* COMMENTS MODAL */}

      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[100]" onClose={closeComments}>
          <TransitionChild as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/35 backdrop-blur-[2px]" />
          </TransitionChild>

          <div className="fixed inset-0 overflow-y-auto p-3 sm:p-5">
            <div className="flex min-h-full items-center justify-center">
              <TransitionChild as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95 translate-y-3" enterTo="opacity-100 scale-100 translate-y-0" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100 translate-y-0" leaveTo="opacity-0 scale-95 translate-y-3">
                <DialogPanel className="relative flex h-[calc(100dvh-24px)] w-full max-w-[860px] flex-col overflow-hidden rounded-3xl border border-gray-100 bg-white text-left shadow-[0_30px_100px_rgba(0,0,0,0.20)] sm:h-[82vh]">

                  {/* HEADER */}

                  <div className="flex shrink-0 items-center justify-between border-b border-gray-100 bg-white px-5 py-4 sm:px-7">
                    <div>
                      <div className="flex items-center gap-2.5">
                        <h2 className="text-xl font-bold text-gray-900">Comments</h2>

                        {comment_numb > 0 && (
                          <span className="flex h-7 min-w-7 items-center justify-center rounded-full bg-purple-50 px-2 text-[11px] font-semibold text-purple-600">
                            {comment_numb}
                          </span>
                        )}
                      </div>

                      <p className="mt-1 text-xs text-gray-400">Join the conversation</p>
                    </div>

                    <button type="button" onClick={closeComments} className="flex h-11 w-11 items-center justify-center rounded-full bg-gray-50 text-gray-500 transition hover:bg-purple-50 hover:text-purple-600 active:scale-95" aria-label="Close comments">
                      <MdClose size={23} />
                    </button>
                  </div>

                  {/* COMMENTS */}

                  <div className="min-h-0 flex-1 overflow-y-auto px-4 sm:px-7">
                    {localComments.length === 0 ? (
                      <div className="flex h-full min-h-[300px] flex-col items-center justify-center text-center">
                        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-purple-50 text-purple-500">
                          <FaRegCommentDots size={27} />
                        </div>

                        <h3 className="mt-4 text-base font-semibold text-gray-900">No comments yet</h3>
                        <p className="mt-1.5 max-w-[280px] text-sm leading-5 text-gray-400">Be the first to start the conversation.</p>
                      </div>
                    ) : (
                      <div className="py-2">
                        {localComments.map((comment) => (
                          <div key={comment?.id} className="relative flex gap-3 py-4 sm:gap-4">

                            {/* AVATAR */}

                            <img src={getMediaUrl(comment?.profile_img)} alt={`${comment?.firstname || ""} ${comment?.lastname || ""}`} className="h-10 w-10 shrink-0 rounded-full object-cover ring-1 ring-gray-100 sm:h-11 sm:w-11" />

                            {/* COMMENT */}

                            <div className="min-w-0 flex-1">

                              {/* USER INFO */}

                              <div className="flex items-start justify-between gap-3">
                                <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                                  <span className="text-sm font-semibold text-gray-900">
                                    {comment?.firstname} {comment?.lastname}
                                  </span>

                                  <span className="text-[11px] text-gray-400">
                                    <TimeAgo date={comment?.created_at} />
                                  </span>
                                </div>

                                {/* OPTIONS */}

                                <Menu as="div" className="relative shrink-0">
                                  <MenuButton className="flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-50 hover:text-gray-700">
                                    <MdMoreHoriz size={20} />
                                  </MenuButton>

                                  <Transition as={Fragment} enter="transition ease-out duration-150" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="transition ease-in duration-100" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                                    <MenuItems className="absolute right-0 z-[100] mt-1 w-44 rounded-xl border border-gray-100 bg-white p-1.5 shadow-xl focus:outline-none">
                                      <MenuItem>
                                        <button type="button" onClick={() => editComment(comment)} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-600 transition data-[focus]:bg-purple-50 data-[focus]:text-purple-600">
                                          <FaEdit size={14} className="text-purple-500" />
                                          Edit comment
                                        </button>
                                      </MenuItem>

                                      <MenuItem>
                                        <button type="button" onClick={() => openDeleteModal(comment)} className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-600 transition data-[focus]:bg-red-50 data-[focus]:text-red-600">
                                          <FaTrash size={13} className="text-red-400" />
                                          Delete comment
                                        </button>
                                      </MenuItem>
                                    </MenuItems>
                                  </Transition>
                                </Menu>
                              </div>

                              {/* CONTENT */}

                              <div className="mt-2 min-w-0">
                                {comment?.comment_type === "text" && (
                                  <div className="inline-block max-w-[560px] rounded-2xl bg-gray-50 px-3.5 py-2.5">
                                    <p className="whitespace-pre-wrap break-words text-sm leading-5 text-gray-700">
                                      {comment?.comment}
                                    </p>
                                  </div>
                                )}

                                {comment?.comment_type === "photo" && <CommentPhoto content={comment?.comment} />}

                                {comment?.comment_type === "video" && <CommentVideo content={comment?.comment} />}

                                {comment?.comment_type === "voice" && <CommentVoice content={comment?.comment} />}
                              </div>

                              {/* COMMENT INFO */}

                              <div className="mt-2">
                                <CommentInfo comment={comment} appearReply={again} setSelectedComment={setSelectedComment} />
                              </div>

                              {/* REPLY INPUT */}

                              <div className="mt-2 flex max-w-[560px] items-center rounded-xl border border-gray-100 bg-gray-50/60 px-2 transition focus-within:border-purple-200 focus-within:bg-white">
                                <input type="text" readOnly placeholder={`Reply to ${comment?.firstname || "comment"}...`} onClick={() => openReply("text", comment)} className="min-w-0 flex-1 cursor-text border-none bg-transparent px-2 py-2 text-xs text-gray-700 outline-none placeholder:text-gray-400 focus:ring-0" />

                                <button type="button" onClick={() => openReply("voice", comment)} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-400 transition hover:bg-orange-50 hover:text-orange-500" aria-label="Voice reply">
                                  <MdKeyboardVoice size={17} />
                                </button>

                                <button type="button" onClick={() => openReply("photo", comment)} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-400 transition hover:bg-green-50 hover:text-green-600" aria-label="Photo reply">
                                  <MdPhotoCamera size={17} />
                                </button>

                                <button type="button" onClick={() => openReply("video", comment)} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-400 transition hover:bg-blue-50 hover:text-blue-600" aria-label="Video reply">
                                  <MdVideocam size={17} />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* CREATE REPLY */}

                  {toggleOpen && <Reply replyType={replyType} comment={selectedComment} closeReply={() => setToggleOpen(false)} />}

                  {/* SHOW REPLIES */}

                  {showReply && <ShowReply comment={selectedComment} closeShowReply={again} />}
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* DELETE COMMENT CONFIRMATION */}

      <Transition appear show={deleteModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-[200]" onClose={closeDeleteModal}>
          <TransitionChild as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-black/45 backdrop-blur-[2px]" />
          </TransitionChild>

          <div className="fixed inset-0 z-[210] flex items-center justify-center p-4">
            <TransitionChild as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95 translate-y-2" enterTo="opacity-100 scale-100 translate-y-0" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100 translate-y-0" leaveTo="opacity-0 scale-95 translate-y-2">
              <DialogPanel className="w-full max-w-sm rounded-3xl border border-gray-100 bg-white p-6 shadow-[0_25px_80px_rgba(0,0,0,0.25)]">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 text-red-500">
                  <FaTrash size={20} />
                </div>

                <div className="mt-5 text-center">
                  <DialogTitle className="text-lg font-bold text-gray-900">Delete comment?</DialogTitle>
                  <p className="mt-2 text-sm leading-6 text-gray-500">Are you sure you want to delete this comment? This action cannot be undone.</p>
                </div>

                <div className="mt-6 flex gap-3">
                  <button type="button" onClick={closeDeleteModal} className="flex-1 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-600 transition hover:bg-gray-50 hover:text-gray-900 active:scale-[0.98]">
                    Cancel
                  </button>

                  <button type="button" onClick={confirmDeleteComment} className="flex-1 rounded-xl bg-red-500 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-red-600 hover:shadow-md active:scale-[0.98]">
                    Delete
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default ShowComment;